// src/components/navigation/FeedFilterBar.js
import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { feedsConfig, createStateFeeds, getStateInfo } from '../../utils/feedsConfig';
import { canadaFeedsConfig } from '../../utils/canadaFeedsConfig';
import { mexicoFeedsConfig } from '../../utils/mexicoFeedsConfig';
import { unitedKingdomFeedsConfig } from '../../utils/unitedKingdomFeedsConfig';

// ─── Paths where the bar is not rendered ───────────────────────────
// Compared case-insensitively against the pathname with any trailing slash
// stripped, so /us/elections-dashboard, /us/elections-dashboard/ and
// /US/Elections-Dashboard all match. The check lives in the component rather
// than in each consumer so every page that mounts the bar honors it without
// having to know about it.
//
// These are the Elections and Spending tabs' own pages, which have no feed to
// filter. NOT /us/elections and /us/spending: those are the Federal feed's
// Elections and Spending topics, and hiding the bar there took the Topic and
// Feed dropdowns away from two ordinary topics.
const HIDDEN_ON_PATHS = new Set([
  '/us/elections-dashboard',
  '/us/spending-dashboard',
]);

function normalizePath(pathname) {
  if (!pathname) return '';
  const trimmed = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  return trimmed.toLowerCase();
}

// ─── Country Configs ───────────────────────────────────────────────
// Keyed by country slug. `schema` is the backend schema name; for the core four
// the slug and the schema differ, for the rest they happen to be identical.
// detectSelection matches on EITHER, so callers can pass a slug ("us") or a
// schema ("united_states_of_america") without silently falling through.
//
// This list must stay in sync with NEW_COUNTRY_SLUGS in IntegratedSearchNavBar
// and with countryConfig.js. A country present in one and missing here used to
// land on the US fallback below; it now renders with no Level dropdown, which
// is correct but still means the country is only half-configured.
const COUNTRY_CONFIGS = {
  us: {
    label: 'United States', shortLabel: 'US', flag: '🇺🇸',
    schema: 'united_states_of_america', feeds: feedsConfig,
  },
  canada: {
    label: 'Canada', shortLabel: 'CA', flag: '🇨🇦',
    schema: 'canada', feeds: canadaFeedsConfig,
  },
  mexico: {
    label: 'Mexico', shortLabel: 'MX', flag: '🇲🇽',
    schema: 'mexico', feeds: mexicoFeedsConfig,
  },
  uk: {
    label: 'United Kingdom', shortLabel: 'UK', flag: '🇬🇧',
    schema: 'united_kingdom', feeds: unitedKingdomFeedsConfig,
  },
  australia: { label: 'Australia', shortLabel: 'AU', flag: '🇦🇺', schema: 'australia', feeds: null },
  china:     { label: 'China',     shortLabel: 'CN', flag: '🇨🇳', schema: 'china',     feeds: null },
  france:    { label: 'France',    shortLabel: 'FR', flag: '🇫🇷', schema: 'france',    feeds: null },
  germany:   { label: 'Germany',   shortLabel: 'DE', flag: '🇩🇪', schema: 'germany',   feeds: null },
  israel:    { label: 'Israel',    shortLabel: 'IL', flag: '🇮🇱', schema: 'israel',    feeds: null },
  italy:     { label: 'Italy',     shortLabel: 'IT', flag: '🇮🇹', schema: 'italy',     feeds: null },
  japan:     { label: 'Japan',     shortLabel: 'JP', flag: '🇯🇵', schema: 'japan',     feeds: null },
  nigeria:   { label: 'Nigeria',   shortLabel: 'NG', flag: '🇳🇬', schema: 'nigeria',   feeds: null },
  'south-africa': { label: 'South Africa', shortLabel: 'ZA', flag: '🇿🇦', schema: 'south-africa', feeds: null },
  ukraine:   { label: 'Ukraine',   shortLabel: 'UA', flag: '🇺🇦', schema: 'ukraine',   feeds: null },
};

// ─── Category Labels ───────────────────────────────────────────────
const CATEGORY_LABELS = {
  recent: 'Recents', legislative: 'Legislative', executive: 'Executive',
  whitehouse: 'White House', judicial: 'Judicial', defense: 'Defense',
  elections: 'Elections', economy: 'Economy', environment: 'Environment',
  foreignAffairs: 'Foreign Affairs', health: 'Health', infrastructure: 'Infrastructure',
  justice: 'Justice', science: 'Science & Tech', regulations: 'Regulations',
  immigration: 'Immigration', spending: 'Spending',
  house: 'House of Representatives', senate: 'Senate',
};

// Updates is the crawl-backed website-updates feed. It's a branch in
// createStateFeeds like the other three, so listing it here is all it takes to
// reach the Topic dropdown.
const STATE_CATEGORY_LABELS = {
  legislative: 'Legislative', executive: 'Executive', judicial: 'Judicial',
  updates: 'Web Crawl Updates',
};

const STRUCTURED_CATEGORIES = ['house', 'senate'];
const HIDDEN_CATEGORIES = ['whitehouse', 'house', 'senate'];

// Topic entries rendered explicitly at the top of the dropdown rather than from
// availableCategories, so they keep a fixed order: Glance, Recents, Following,
// Live streams, Updates, All Categories. Anything listed here is filtered out of the
// lower list so it can't appear twice.
//
// 'updates' is pinned federally only. In state mode it's an ordinary branch of
// createStateFeeds sitting alongside Legislative / Executive / Judicial, so it
// belongs in the lower list with them rather than up top with the page-owned
// views — see `pinnedCategories` in the component, which drops it there.
const PINNED_CATEGORIES = ['recent', 'updates'];

// Categories the *page* owns rather than the feed config: they have no table to
// load, so the consumer can't navigate to them and the Feed dropdown is
// meaningless for them. 'updates' is the exception that has to be asked about
// rather than listed — federally it's page-owned, but in state mode it's a real
// branch of createStateFeeds with real feeds behind it.
//
// 'videos' is the US home page's Live streams view (VideoFeed, /us/live-streams): live, scheduled and
// recorded government streams from api2, with no feed config behind it.
const PAGE_OWNED_CATEGORIES = ['glance', 'following', 'recent', 'videos'];

function isPageOwnedCategory(category, inStateMode) {
  if (!category) return false;
  if (category === 'updates') return !inStateMode;
  return PAGE_OWNED_CATEGORIES.includes(category);
}

// ─── States / Provinces / Regions ─────────────────────────────────
const US_STATES_DATA = [
  { name: 'Alabama', flag: '/alabamaFlag.png', key: 'alabama' },
  { name: 'Alaska', flag: '/alaskaFlag.png', key: 'alaska' },
  { name: 'Arizona', flag: '/arizonaFlag.png', key: 'arizona' },
  { name: 'Arkansas', flag: '/arkansasFlag.png', key: 'arkansas' },
  { name: 'California', flag: '/californiaFlag.png', key: 'california' },
  { name: 'Colorado', flag: '/coloradoFlag.png', key: 'colorado' },
  { name: 'Connecticut', flag: '/connecticutFlag.png', key: 'connecticut' },
  { name: 'Delaware', flag: '/delawareFlag.png', key: 'delaware' },
  { name: 'Florida', flag: '/floridaFlag.png', key: 'florida' },
  { name: 'Georgia', flag: '/georgiaFlag.png', key: 'georgia' },
  { name: 'Hawaii', flag: '/hawaiiFlag.png', key: 'hawaii' },
  { name: 'Idaho', flag: '/idahoFlag.png', key: 'idaho' },
  { name: 'Illinois', flag: '/illinoisFlag.png', key: 'illinois' },
  { name: 'Indiana', flag: '/indianaFlag.png', key: 'indiana' },
  { name: 'Iowa', flag: '/iowaFlag.png', key: 'iowa' },
  { name: 'Kansas', flag: '/kansasFlag.png', key: 'kansas' },
  { name: 'Kentucky', flag: '/kentuckyFlag.png', key: 'kentucky' },
  { name: 'Louisiana', flag: '/louisianaFlag.png', key: 'louisiana' },
  { name: 'Maine', flag: '/maineFlag.png', key: 'maine' },
  { name: 'Maryland', flag: '/marylandFlag.png', key: 'maryland' },
  { name: 'Massachusetts', flag: '/massachusettsFlag.png', key: 'massachusetts' },
  { name: 'Michigan', flag: '/michiganFlag.png', key: 'michigan' },
  { name: 'Minnesota', flag: '/minnesotaFlag.png', key: 'minnesota' },
  { name: 'Mississippi', flag: '/mississippiFlag.png', key: 'mississippi' },
  { name: 'Missouri', flag: '/missouriFlag.png', key: 'missouri' },
  { name: 'Montana', flag: '/montanaFlag.png', key: 'montana' },
  { name: 'Nebraska', flag: '/nebraskaFlag.png', key: 'nebraska' },
  { name: 'Nevada', flag: '/nevadaFlag.png', key: 'nevada' },
  { name: 'New Hampshire', flag: '/newHampshireFlag.png', key: 'newhampshire' },
  { name: 'New Jersey', flag: '/newJerseyFlag.png', key: 'newjersey' },
  { name: 'New Mexico', flag: '/newMexicoFlag.png', key: 'newmexico' },
  { name: 'New York', flag: '/newYorkFlag.png', key: 'newyork' },
  { name: 'North Carolina', flag: '/northCarolinaFlag.png', key: 'northcarolina' },
  { name: 'North Dakota', flag: '/northDakotaFlag.png', key: 'northdakota' },
  { name: 'Ohio', flag: '/ohioFlag.png', key: 'ohio' },
  { name: 'Oklahoma', flag: '/oklahomaFlag.png', key: 'oklahoma' },
  { name: 'Oregon', flag: '/oregonFlag.png', key: 'oregon' },
  { name: 'Pennsylvania', flag: '/pennsylvaniaFlag.png', key: 'pennsylvania' },
  { name: 'Rhode Island', flag: '/rhodeIslandFlag.png', key: 'rhodeisland' },
  { name: 'South Carolina', flag: '/southCarolinaFlag.png', key: 'southcarolina' },
  { name: 'South Dakota', flag: '/southDakotaFlag.png', key: 'southdakota' },
  { name: 'Tennessee', flag: '/tennesseeFlag.png', key: 'tennessee' },
  { name: 'Texas', flag: '/texasFlag.png', key: 'texas' },
  { name: 'Utah', flag: '/utahFlag.png', key: 'utah' },
  { name: 'Vermont', flag: '/vermontFlag.png', key: 'vermont' },
  { name: 'Virginia', flag: '/virginiaFlag.png', key: 'virginia' },
  { name: 'Washington', flag: '/washingtonFlag.png', key: 'washington' },
  { name: 'West Virginia', flag: '/westVirginiaFlag.png', key: 'westvirginia' },
  { name: 'Wisconsin', flag: '/wisconsinFlag.png', key: 'wisconsin' },
  { name: 'Wyoming', flag: '/wyomingFlag.png', key: 'wyoming' },
];

const MEXICO_STATES_DATA = [
  { name: 'Aguascalientes',     flag: '/aguascalientesFlag.png',    key: 'aguascalientes' },
  { name: 'Baja California',    flag: '/bajaCaliforniaFlag.png',    key: 'bajacalifornia' },
  { name: 'Baja California Sur',flag: '/bajaCaliforniaSurFlag.png', key: 'bajacaliforniasur' },
  { name: 'Campeche',           flag: '/campecheFlag.png',          key: 'campeche' },
  { name: 'Chiapas',            flag: '/chiapasFlag.png',           key: 'chiapas' },
  { name: 'Chihuahua',          flag: '/chihuahuaFlag.png',         key: 'chihuahua' },
  { name: 'Coahuila',           flag: '/coahuilaFlag.png',          key: 'coahuila' },
  { name: 'Colima',             flag: '/colimaFlag.png',            key: 'colima' },
  { name: 'Durango',            flag: '/durangoFlag.png',           key: 'durango' },
  { name: 'Guanajuato',         flag: '/guanajuatoFlag.png',        key: 'guanajuato' },
  { name: 'Guerrero',           flag: '/guerreroFlag.png',          key: 'guerrero' },
  { name: 'Hidalgo',            flag: '/hidalgoFlag.png',           key: 'hidalgo' },
  { name: 'Jalisco',            flag: '/jaliscoFlag.png',           key: 'jalisco' },
  { name: 'Ciudad de México',   flag: '/mexicoCityFlag.png',        key: 'mexicocity' },
  { name: 'Estado de México',   flag: '/mexicoStateFlag.png',       key: 'mexicostate' },
  { name: 'Michoacán',          flag: '/michoacanFlag.png',         key: 'michoacan' },
  { name: 'Morelos',            flag: '/morelosFlag.png',           key: 'morelos' },
  { name: 'Nayarit',            flag: '/nayaritFlag.png',           key: 'nayarit' },
  { name: 'Nuevo León',         flag: '/nuevoLeonFlag.png',         key: 'nuevoleon' },
  { name: 'Oaxaca',             flag: '/oaxacaFlag.png',            key: 'oaxaca' },
  { name: 'Puebla',             flag: '/pueblaFlag.png',            key: 'puebla' },
  { name: 'Querétaro',          flag: '/queretaroFlag.png',         key: 'queretaro' },
  { name: 'Quintana Roo',       flag: '/quintanaRooFlag.png',       key: 'quintanaroo' },
  { name: 'San Luis Potosí',    flag: '/sanLuisPotosiFlag.png',     key: 'sanluispotosi' },
  { name: 'Sinaloa',            flag: '/sinaloaFlag.png',           key: 'sinaloa' },
  { name: 'Sonora',             flag: '/sonoraFlag.png',            key: 'sonora' },
  { name: 'Tabasco',            flag: '/tabascoFlag.png',           key: 'tabasco' },
  { name: 'Tamaulipas',         flag: '/tamaulipasFlag.png',        key: 'tamaulipas' },
  { name: 'Tlaxcala',           flag: '/tlaxcalaFlag.png',          key: 'tlaxcala' },
  { name: 'Veracruz',           flag: '/veracruzFlag.png',          key: 'veracruz' },
  { name: 'Yucatán',            flag: '/yucatanFlag.png',           key: 'yucatan' },
  { name: 'Zacatecas',          flag: '/zacatecasFlag.png',         key: 'zacatecas' },
];

const UK_REGIONS_DATA = [
  { name: 'England',          flag: '/englandFlag.png',         key: 'england' },
  { name: 'Scotland',         flag: '/scotlandFlag.png',        key: 'scotland' },
  { name: 'Wales',            flag: '/walesFlag.png',           key: 'wales' },
  { name: 'Northern Ireland', flag: '/northernIrelandFlag.png', key: 'northernireland' },
];

const CANADA_PROVINCES_DATA = [
  { name: 'Alberta',                   flag: '/albertaFlag.png',                 key: 'alberta' },
  { name: 'British Columbia',          flag: '/britishColumbiaFlag.png',         key: 'british_columbia' },
  { name: 'Manitoba',                  flag: '/manitobaFlag.png',                key: 'manitoba' },
  { name: 'New Brunswick',             flag: '/newBrunswickFlag.png',            key: 'new_brunswick' },
  { name: 'Newfoundland and Labrador', flag: '/newfoundlandAndLabradorFlag.png', key: 'newfoundland_labrador' },
  { name: 'Northwest Territories',     flag: '/northwestTerritoriesFlag.png',    key: 'northwest_territories' },
  { name: 'Nova Scotia',               flag: '/novaScotiaFlag.png',              key: 'nova_scotia' },
  { name: 'Nunavut',                   flag: '/nunavutFlag.png',                 key: 'nunavut' },
  { name: 'Ontario',                   flag: '/ontarioFlag.png',                 key: 'ontario' },
  { name: 'Prince Edward Island',      flag: '/princeEdwardIslandFlag.png',      key: 'prince_edward_island' },
  { name: 'Quebec',                    flag: '/quebecFlag.png',                  key: 'quebec' },
  { name: 'Saskatchewan',              flag: '/saskatchewanFlag.png',            key: 'saskatchewan' },
  { name: 'Yukon',                     flag: '/yukonFlag.png',                   key: 'yukon' },
];

// Single source of truth for "does this country have sub-national regions".
// A country absent from this map gets no Level dropdown at all. Do NOT give it
// a default: an unknown country previously fell through to US_STATES_DATA,
// which is how Australia ended up showing a list of US states.
const COUNTRY_REGIONS = {
  us: US_STATES_DATA,
  canada: CANADA_PROVINCES_DATA,
  mexico: MEXICO_STATES_DATA,
  uk: UK_REGIONS_DATA,
};

// Countries indexed by crawl.govglance.org. Absent from this set means there is
// no website-updates data behind the topic, so it isn't offered at all rather
// than sitting in the dropdown loading nothing — that's why Mexico, which the
// crawler doesn't cover, has no Updates entry. Add a slug here the moment the
// crawler starts indexing it; the country home page needs its own updates view
// at the same time, or the topic selects a view that doesn't exist.
const CRAWL_COUNTRIES = new Set(['us', 'canada', 'uk']);

// Countries the streams monitor covers. Only the US today: every channel it
// watches is linked from a US government site.
const VIDEO_COUNTRIES = new Set(['us']);

// State-level feed configs only exist for US states today. Every other country
// can pick a region but still reads its federal feeds, so "state mode" — the
// mode where the Topic dropdown lists Legislative / Executive / Judicial /
// Updates instead of the federal categories — is US-only.
function isStateMode(country, stateKeyArg) {
  return country === 'us' && !!stateKeyArg;
}

// ─── Helpers ───────────────────────────────────────────────────────

function parseApiEndpoint(apiEndpoint) {
  if (!apiEndpoint) return {};
  const params = new URLSearchParams(apiEndpoint.replace(/^&/, ''));
  return {
    table: params.get('table') || params.get('table_name') || '',
    schema: params.get('schema') || '',
    filterColumn: params.get('filter_column') || '',
    filterString: params.get('filter_string') || '',
    orderBy: params.get('order_by') || '',
    committee: params.get('committee') || '',
    chamber: params.get('chamber') || '',
  };
}

function flattenFeedsForCategory(categoryFeeds) {
  if (!Array.isArray(categoryFeeds)) return [];
  const result = [];
  categoryFeeds.forEach((feed) => {
    if (feed.type === 'normal') {
      result.push({
        id: feed.id, title: feed.title, feedName: feed.feedName,
        apiEndpoint: feed.apiEndpoint, branch: feed.branch, topic: feed.topic,
        buttonName: feed.buttonName, viewAllLink: feed.viewAllLink,
        useCommitteeAPI: feed.useCommitteeAPI || false,
      });
    } else if (feed.type === 'carousel' && feed.carouselItems) {
      feed.carouselItems.forEach((item) => {
        if (item.type === 'viewall' || item.type === 'member') return;
        result.push({
          id: item.id, title: `${feed.departmentTitle} — ${item.title}`,
          feedName: item.feedName, apiEndpoint: item.apiEndpoint,
          branch: item.branch || feed.branch || 'N/A',
          topic: item.topic || feed.topic || 'N/A',
          buttonName: item.buttonName || '', viewAllLink: feed.departmentLink,
          useCommitteeAPI: item.useCommitteeAPI || false,
        });
      });
    }
  });
  return result;
}

function flattenStructuredCategory(categoryObj) {
  if (!categoryObj || !categoryObj.committees) return [];
  const result = [];
  categoryObj.committees.forEach((committee) => {
    if (committee.meetingFeed) {
      result.push({
        id: `${committee.id}-meetings`, title: `${committee.shortName} — Meetings`,
        feedName: committee.meetingFeed.feedName, apiEndpoint: committee.meetingFeed.apiEndpoint,
        branch: 'Legislative', topic: 'N/A', buttonName: committee.meetingFeed.buttonName,
        viewAllLink: committee.officialURL, useCommitteeAPI: committee.meetingFeed.useCommitteeAPI || false,
      });
    }
    if (committee.materialsFeed) {
      result.push({
        id: `${committee.id}-materials`, title: `${committee.shortName} — Materials`,
        feedName: committee.materialsFeed.feedName, apiEndpoint: committee.materialsFeed.apiEndpoint,
        branch: 'Legislative', topic: 'N/A', buttonName: committee.materialsFeed.buttonName,
        viewAllLink: committee.officialURL, useCommitteeAPI: committee.materialsFeed.useCommitteeAPI || false,
      });
    }
    if (committee.newsFeed) {
      result.push({
        id: `${committee.id}-news`, title: `${committee.shortName} — News`,
        feedName: committee.newsFeed.feedName, apiEndpoint: committee.newsFeed.apiEndpoint,
        branch: 'Legislative', topic: 'N/A', buttonName: committee.newsFeed.buttonName,
        viewAllLink: committee.officialURL, useCommitteeAPI: committee.newsFeed.useCommitteeAPI || false,
      });
    }
  });
  return result;
}

function normalizeStateFeedsForCategory(stateFeedsArray) {
  if (!Array.isArray(stateFeedsArray)) return [];
  return stateFeedsArray.map((feed) => ({
    id: feed.id, title: feed.name, feedName: feed.name,
    // A feed with no apiParams would otherwise produce the string "&undefined",
    // which parseApiEndpoint would then happily parse into nonsense.
    apiEndpoint: feed.apiParams ? `&${feed.apiParams}` : null,
    branch: feed.feedBranch || 'N/A', topic: feed.feedTopic || 'N/A',
    buttonName: feed.buttonName || 'Document', viewAllLink: null,
    useCommitteeAPI: false, isStateFeed: true,
  }));
}

// A feed can only be handed to a consumer that loads tables (FullFeedPage) if
// we can actually resolve a table out of it. Committee feeds carry their own
// endpoint shape and are loadable too.
function isLoadableFeed(feed) {
  if (!feed || !feed.apiEndpoint) return false;
  if (feed.useCommitteeAPI) return true;
  return !!parseApiEndpoint(feed.apiEndpoint).table;
}

function firstLoadableFeed(feeds) {
  if (!Array.isArray(feeds)) return null;
  return feeds.find(isLoadableFeed) || null;
}

function scoreFeedMatch(feed, currentTable, currentFilterColumn, currentFilterString, currentCommittee) {
  const parsed = parseApiEndpoint(feed.apiEndpoint);
  let score = 0;
  if (currentCommittee && feed.useCommitteeAPI) {
    if (parsed.committee && decodeURIComponent(parsed.committee) === currentCommittee) score = 10;
  } else if (currentTable) {
    if (parsed.table === currentTable) {
      score = 1;
      if (currentFilterColumn && currentFilterString) {
        if (parsed.filterColumn === currentFilterColumn &&
            decodeURIComponent(parsed.filterString) === decodeURIComponent(currentFilterString)) score += 5;
      } else if (!parsed.filterColumn) { score += 1; }
    }
  }
  return score;
}

// Accepts either a country slug ("us", "australia") or a backend schema
// ("united_states_of_america"). Routes like /:schema/recents hand us the slug,
// while feed pages hand us the schema; matching on both keeps either caller
// from falling through to the "unknown country" branch.
function resolveCountryKey(schemaOrSlug) {
  if (!schemaOrSlug) return null;
  if (COUNTRY_CONFIGS[schemaOrSlug]) return schemaOrSlug;
  for (const [key, cfg] of Object.entries(COUNTRY_CONFIGS)) {
    if (cfg.schema === schemaOrSlug) return key;
  }
  return null;
}

// ─── Selection sources ─────────────────────────────────────────────
// getCategoriesFor / getFeedsFor are pure: given a (country, state, category)
// triple they say what the next dropdown down should contain. They're used both
// to render the dropdowns and to resolve the cascade when a selection changes,
// so the list the user sees and the feed we auto-load can't drift apart.

function getCategoriesFor(country, stateKeyArg, { hideRecents = false, hideUpdates = false } = {}) {
  if (isStateMode(country, stateKeyArg)) {
    const stateFeeds = createStateFeeds(stateKeyArg);
    if (!stateFeeds) return [];
    return Object.keys(stateFeeds)
      .filter((key) => STATE_CATEGORY_LABELS[key] && stateFeeds[key].length > 0)
      .filter((key) => !(hideUpdates && key === 'updates'))
      .map((key) => ({ key, label: STATE_CATEGORY_LABELS[key] }));
  }
  const cfg = country ? COUNTRY_CONFIGS[country] : null;
  if (!cfg || !cfg.feeds) return [];
  return Object.keys(cfg.feeds)
    .filter((key) => CATEGORY_LABELS[key] !== undefined)
    .filter((key) => !HIDDEN_CATEGORIES.includes(key))
    .filter((key) => !(hideRecents && key === 'recent'))
    .map((key) => ({ key, label: CATEGORY_LABELS[key] }));
}

function getFeedsFor(country, stateKeyArg, category, { hideRecents = false, hideUpdates = false } = {}) {
  if (isStateMode(country, stateKeyArg)) {
    const stateFeeds = createStateFeeds(stateKeyArg);
    if (!stateFeeds) return [];
    if (category) {
      if (hideUpdates && category === 'updates') return [];
      return stateFeeds[category] ? normalizeStateFeedsForCategory(stateFeeds[category]) : [];
    }
    const all = [];
    for (const [catKey, feeds] of Object.entries(stateFeeds)) {
      if (!STATE_CATEGORY_LABELS[catKey]) continue;
      if (hideUpdates && catKey === 'updates') continue;
      normalizeStateFeedsForCategory(feeds).forEach((f) => all.push(f));
    }
    return all;
  }

  const cfg = country ? COUNTRY_CONFIGS[country] : null;
  if (!cfg || !cfg.feeds) return [];

  if (category) {
    if (category === 'recent') {
      if (hideRecents) return [];
      return [{ id: 'recent_all', title: 'Recents', feedName: 'Recents', apiEndpoint: null }];
    }
    if (category === 'glance') return [];
    if (category === 'following') return [];
    // Live streams is the page's stream feed, not a config category — no Feed
    // dropdown to offer.
    if (category === 'videos') return [];
    // Federal Updates is a single crawl-backed feed owned by the page, not a
    // config category — no Feed dropdown to offer.
    if (category === 'updates') return [];
    const categoryData = cfg.feeds[category];
    if (!categoryData) return [];
    if (STRUCTURED_CATEGORIES.includes(category)) return flattenStructuredCategory(categoryData);
    return flattenFeedsForCategory(categoryData);
  }

  const all = [];
  for (const [catKey, catData] of Object.entries(cfg.feeds)) {
    if (CATEGORY_LABELS[catKey] === undefined) continue;
    if (catKey === 'recent') {
      if (!hideRecents) all.push({ id: 'recent_all', title: 'Recents', feedName: 'Recents', apiEndpoint: null });
      continue;
    }
    const feeds = STRUCTURED_CATEGORIES.includes(catKey)
      ? flattenStructuredCategory(catData)
      : flattenFeedsForCategory(catData);
    feeds.forEach((f) => all.push(f));
  }
  return all;
}

// ─── Detection from the current URL ────────────────────────────────

function detectSelection({ currentSchema, currentTable, currentFilterColumn, currentFilterString, currentCommittee }) {
  const detectedCountry = resolveCountryKey(currentSchema);
  if (!detectedCountry) return { country: null, stateKey: null, category: null, feedId: null };

  const countryConfig = COUNTRY_CONFIGS[detectedCountry];

  let federalBest = { score: 0, category: null, feedId: null };
  if (countryConfig.feeds) {
    for (const [catKey, catData] of Object.entries(countryConfig.feeds)) {
      if (CATEGORY_LABELS[catKey] === undefined) continue;
      const feeds = STRUCTURED_CATEGORIES.includes(catKey)
        ? flattenStructuredCategory(catData) : flattenFeedsForCategory(catData);
      for (const feed of feeds) {
        const score = scoreFeedMatch(feed, currentTable, currentFilterColumn, currentFilterString, currentCommittee);
        if (score > federalBest.score) federalBest = { score, category: catKey, feedId: feed.id };
      }
    }
  }

  // State feeds are now scanned in full before anything is returned. The old
  // code returned from inside the loop the moment a feed beat the running
  // score, so the first state that merely shared a table (Alabama, since the
  // list is alphabetical) shadowed the state whose filter_string actually
  // matched the URL. That is the bug behind "the bar shows the wrong state" and
  // behind Federal winning when it shouldn't: the federal scan also used to
  // return early, so state feeds were never even considered whenever any
  // federal feed touched the same table.
  //
  // Ties are still possible when several states share one table and the URL
  // carries no filter to tell them apart. The authoritative answer in that case
  // is the `stateKey` prop, which FullFeedPage supplies from the ggState query
  // param; detection is only the fallback for a cold load or a shared link.
  let stateBest = { score: 0, stateKey: null, category: null, feedId: null };
  if (detectedCountry === 'us' && currentTable) {
    for (const stateEntry of US_STATES_DATA) {
      const stateFeeds = createStateFeeds(stateEntry.key);
      if (!stateFeeds) continue;
      for (const [catKey, feeds] of Object.entries(stateFeeds)) {
        if (!STATE_CATEGORY_LABELS[catKey]) continue;
        const normalized = normalizeStateFeedsForCategory(feeds);
        for (const feed of normalized) {
          const score = scoreFeedMatch(feed, currentTable, currentFilterColumn, currentFilterString, '');
          if (score > stateBest.score) {
            stateBest = { score, stateKey: stateEntry.key, category: catKey, feedId: feed.id };
          }
        }
      }
    }
  }

  // Federal is the default, so a state match has to beat it outright rather
  // than merely tie it.
  if (stateBest.score > federalBest.score) {
    return {
      country: detectedCountry,
      stateKey: stateBest.stateKey,
      category: stateBest.category,
      feedId: stateBest.feedId,
    };
  }

  return {
    country: detectedCountry,
    stateKey: null,
    category: federalBest.category,
    feedId: federalBest.feedId,
  };
}

// ─── Dropdown viewport alignment ───────────────────────────────────
// Panels are sized by their content (see .feed-filter-dropdown in the CSS), so
// a panel opened from a trigger near the right edge would extend past the
// screen. CSS can't fix this: `right: 0` on a fixed set of triggers is a guess
// at their position, and it fails outright for a middle trigger whose panel is
// simply wider than the space to its right.
//
// So we measure. Each panel starts left-aligned to its own trigger, then shifts
// left by exactly the overflow amount — no further. A panel that already fits
// doesn't move at all, which keeps it visually tied to the control that opened
// it rather than snapping every panel to one edge.

const VIEWPORT_MARGIN = 12;

function useDropdownAlignment(isOpen, anchorRef) {
  const panelRef = useRef(null);
  // null means "not measured yet" — the CSS default applies until layout runs.
  const [offset, setOffset] = useState(null);

  // useLayoutEffect, not useEffect: this runs before paint, so the panel never
  // renders once in the wrong place and then visibly jumps.
  useLayoutEffect(() => {
    if (!isOpen) { setOffset(null); return undefined; }

    const align = () => {
      const panel = panelRef.current;
      const anchor = anchorRef.current;
      if (!panel || !anchor) return;

      // The anchor is the positioned parent, so `left` is relative to it.
      const anchorRect = anchor.getBoundingClientRect();
      const panelWidth = panel.offsetWidth;

      let next = 0;
      const overflowRight = (anchorRect.left + panelWidth) - (window.innerWidth - VIEWPORT_MARGIN);
      if (overflowRight > 0) next = -overflowRight;

      // Pulling left can overshoot on narrow screens; clamp to the left edge.
      // The panel's max-width guarantees it fits once both edges are respected.
      if (anchorRect.left + next < VIEWPORT_MARGIN) next = VIEWPORT_MARGIN - anchorRect.left;

      setOffset(next);
    };

    align();
    window.addEventListener('resize', align);
    window.addEventListener('orientationchange', align);
    return () => {
      window.removeEventListener('resize', align);
      window.removeEventListener('orientationchange', align);
    };
  }, [isOpen, anchorRef]);

  // `right: auto` is needed for the Sort panel, whose CSS anchors it right.
  const panelStyle = offset === null ? undefined : { left: offset, right: 'auto' };
  return [panelRef, panelStyle];
}

// ─── Reusable Custom Dropdown ──────────────────────────────────────

function FilterDropdown({ label, isOpen, onToggle, displayContent, children }) {
  const ref = useRef(null);
  const [panelRef, panelStyle] = useDropdownAlignment(isOpen, ref);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        if (isOpen) onToggle();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onToggle]);

  return (
    <div className="feed-filter-group" ref={ref}>
      <span className="feed-filter-label">{label}</span>
      <button type="button" className="feed-filter-toggle" onClick={onToggle}>
        <span className="feed-filter-toggle-content">{displayContent}</span>
        <span className="feed-filter-chevron" />
      </button>
      {isOpen && (
        <div className="feed-filter-dropdown" ref={panelRef} style={panelStyle}>
          {children}
        </div>
      )}
    </div>
  );
}

function FilterItem({ active, onClick, children, image, emoji }) {
  return (
    <button type="button" className={`feed-filter-item ${active ? 'active' : ''}`} onClick={onClick}>
      {image && <img src={image} alt="" className="feed-filter-item-img" />}
      {emoji && <span className="feed-filter-item-emoji">{emoji}</span>}
      <span>{children}</span>
    </button>
  );
}

function FilterSeparator() {
  return <div className="feed-filter-separator" />;
}

// ─── Date field detection ──────────────────────────────────────────

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}(T|\s)/;

function formatFieldLabel(key) {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function detectDateFields(data) {
  if (!Array.isArray(data) || data.length === 0) return [];
  const sample = data[0];
  const fields = [];
  for (const [key, value] of Object.entries(sample)) {
    if (value == null) continue;
    if (typeof value === 'string' && ISO_DATE_RE.test(value)) {
      fields.push({ key, label: formatFieldLabel(key) });
    }
  }
  fields.sort((a, b) => a.label.localeCompare(b.label));
  return fields;
}

// ─── Main Component ────────────────────────────────────────────────

export default function FeedFilterBar({
  onFilterChange,
  currentSchema = '',
  currentTable = '',
  currentFilterColumn = '',
  currentFilterString = '',
  currentCommittee = '',
  data = [],
  currentOrderBy = '',
  allowedOrderBy = [],
  onSortChange,
  hideCountry = false,
  inline = false,
  className = '',
  currentCategory = null,
  hideSortButton = false,
  hideLevel = false,
  hideRecents = false,
  // Forces state mode for a page that already knows which state it is showing
  // (StatePage, and FullFeedPage via the ggState query param), instead of
  // inferring it from currentTable. With this set the Topic dropdown lists the
  // state's branches — Legislative / Executive / Judicial / Updates — rather
  // than the federal categories.
  stateKey = null,
  // Following reads from the federal feed config, so state pages hide it.
  hideFollowing = false,
  // Updates is crawl-backed: its feeds carry `crawl=recent` and no schema/table,
  // both federally and per state. A consumer that can only render table-backed
  // feeds (FullFeedPage, which routes to /feed/:schema/:table) has nothing to
  // load for it, so it sets this and the topic doesn't appear at all rather
  // than sitting there as a dead click.
  //
  // This is the consumer's half of the decision. The other half is the country:
  // a country outside CRAWL_COUNTRIES has no crawl data at all, so Updates is
  // hidden there regardless of what the consumer passes. See `updatesHidden`.
  hideUpdates = false,
  // Same idea for the AI-ranked Glance view, which is rendered by the page
  // itself rather than fetched from a feed config.
  hideGlance = false,
  // The Live streams topic (live, scheduled and recorded government streams). Opt-in
  // rather than opt-out, because only a page that renders VideoFeed can do
  // anything with it — the US home page — and every other consumer would get
  // a dead entry. The country must also be one the streams monitor covers;
  // see `videosAvailable`.
  showVideos = false,
  // When true, changing Country / Level / Topic resolves the rest of the
  // cascade and reports a concrete feed, so the consumer can load data on every
  // selection instead of waiting for the user to reach the Feed dropdown.
  // Pages that treat a bare category change as "just update the dropdowns"
  // (and load nothing) can turn this off.
  autoSelectFeed = true,
}) {
  const { t } = useTranslation('common');
  const { pathname } = useLocation();
  const hiddenOnThisPath = HIDDEN_ON_PATHS.has(normalizePath(pathname));

  const detected = useMemo(() => detectSelection({
    currentSchema, currentTable, currentFilterColumn, currentFilterString, currentCommittee,
  }), [currentSchema, currentTable, currentFilterColumn, currentFilterString, currentCommittee]);

  // An unrecognized schema stays null rather than defaulting to 'us'. Every
  // consumer below treats null as "no country selected"; nothing silently
  // renders US data for a country we don't recognize.
  const [selectedCountry, setSelectedCountry] = useState(detected.country);
  const [selectedState, setSelectedState] = useState(stateKey || detected.stateKey || null);
  const [selectedCategory, setSelectedCategory] = useState(detected.category);
  const [selectedFeedId, setSelectedFeedId] = useState(detected.feedId);
  const [openDropdown, setOpenDropdown] = useState(null);

  const toggleDropdown = useCallback((name) => {
    setOpenDropdown((prev) => (prev === name ? null : name));
  }, []);

  const closeAll = useCallback(() => setOpenDropdown(null), []);

  const sortRef = useRef(null);
  const [sortPanelRef, sortPanelStyle] = useDropdownAlignment(openDropdown === 'sort', sortRef);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (openDropdown === 'sort' && sortRef.current && !sortRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdown]);

  // Re-sync from the URL after the consumer navigates. `stateKey` wins over
  // detection: a page that knows which state it is showing is never overruled
  // by a table-name guess.
  useEffect(() => {
    setSelectedCountry(detected.country);
    setSelectedState(stateKey || detected.stateKey || null);
    setSelectedCategory(detected.category);
    setSelectedFeedId(detected.feedId);
  }, [detected.country, detected.stateKey, detected.category, detected.feedId, stateKey]);

  useEffect(() => {
    if (currentCategory) {
      setSelectedCategory(currentCategory);
      setSelectedFeedId(null);
    }
  }, [currentCategory]);

  const countryConfig = selectedCountry ? COUNTRY_CONFIGS[selectedCountry] : null;

  // Regions for the selected country, or null when the country has none (or is
  // unknown). This drives both the Level dropdown's visibility and its contents,
  // so the two can't disagree.
  const regionsForCountry = selectedCountry ? COUNTRY_REGIONS[selectedCountry] : null;
  const hasRegions = Array.isArray(regionsForCountry) && regionsForCountry.length > 0;

  const isInStateMode = isStateMode(selectedCountry, selectedState);

  // Whether Updates is offered at all: the consumer can veto it, and so can the
  // country. Everything below reads this rather than the raw prop, so the topic
  // list, the cascade and the rendered item can't disagree about it. A null
  // country (unrecognized schema) is not in the set, so it hides too.
  const updatesHidden = hideUpdates || !CRAWL_COUNTRIES.has(selectedCountry);

  // Whether Live streams is offered: the consumer asked for it, the country is one the
  // streams monitor covers, and the bar is at the national level (a state
  // page's topics are that state's branches).
  const videosAvailable = showVideos && VIDEO_COUNTRIES.has(selectedCountry) && !isInStateMode;

  // Which topics get rendered up top instead of in the lower list. In state mode
  // Updates comes out of this set: it's a branch like Legislative / Executive /
  // Judicial there, so it renders with them, in the order createStateFeeds
  // defines. Federally it stays pinned, since there is no config category to
  // put it in the lower list at all.
  const pinnedCategories = useMemo(
    () => (isInStateMode ? PINNED_CATEGORIES.filter((key) => key !== 'updates') : PINNED_CATEGORIES),
    [isInStateMode]
  );

  const availableCategories = useMemo(
    () => getCategoriesFor(selectedCountry, selectedState, { hideRecents, hideUpdates: updatesHidden }),
    [selectedCountry, selectedState, hideRecents, updatesHidden]
  );

  const availableFeeds = useMemo(
    () => getFeedsFor(selectedCountry, selectedState, selectedCategory, { hideRecents, hideUpdates: updatesHidden }),
    [selectedCountry, selectedState, selectedCategory, hideRecents, updatesHidden]
  );

  const hasFederalFeeds = !!(countryConfig && countryConfig.feeds);

  const fireChange = useCallback((country, stateKeyArg, category, feedId, feeds) => {
    if (!onFilterChange) return;
    const cfg = country ? COUNTRY_CONFIGS[country] : null;
    const selectedFeed = feeds.find((f) => f.id === feedId) || null;
    const stateData = stateKeyArg ? (getStateInfo(stateKeyArg) || null) : null;
    onFilterChange({
      country, countryConfig: cfg || null, schema: cfg?.schema || null,
      stateKey: stateKeyArg || null, stateInfo: stateData, category, feed: selectedFeed,
    });
  }, [onFilterChange]);

  // Walk the cascade down from whatever the user just picked and return the
  // first thing that can actually be loaded.
  //
  // `drillIntoCategory` is the difference between "you changed Country or
  // Level, so pick a topic for me" and "you explicitly chose All Categories,
  // so leave the topic alone". In the second case the topic stays exactly as
  // asked and only the feed is resolved.
  //
  // Note this resolves against the country being switched TO, not the one in
  // state, so it takes its own view of whether Updates is available rather than
  // reading `updatesHidden`.
  const resolveCascade = useCallback((country, stateKeyArg, category, { drillIntoCategory }) => {
    const opts = {
      hideRecents,
      hideUpdates: hideUpdates || !CRAWL_COUNTRIES.has(country),
    };
    const inStateMode = isStateMode(country, stateKeyArg);

    if (!autoSelectFeed) {
      return { category: category || null, feeds: getFeedsFor(country, stateKeyArg, category, opts), feed: null };
    }

    // Page-owned topics have no table behind them; report them as-is and let
    // the consumer decide what to render.
    if (isPageOwnedCategory(category, inStateMode)) {
      return { category, feeds: getFeedsFor(country, stateKeyArg, category, opts), feed: null };
    }

    if (category || !drillIntoCategory) {
      const feeds = getFeedsFor(country, stateKeyArg, category || null, opts);
      return { category: category || null, feeds, feed: firstLoadableFeed(feeds) };
    }

    for (const cat of getCategoriesFor(country, stateKeyArg, opts)) {
      if (isPageOwnedCategory(cat.key, inStateMode)) continue;
      const feeds = getFeedsFor(country, stateKeyArg, cat.key, opts);
      const feed = firstLoadableFeed(feeds);
      if (feed) return { category: cat.key, feeds, feed };
    }

    return { category: null, feeds: [], feed: null };
  }, [autoSelectFeed, hideRecents, hideUpdates]);

  const handleFollowingClick = useCallback(() => {
    const isActive = currentCategory === 'following';
    if (!onFilterChange) return;
    onFilterChange({
      country: selectedCountry,
      countryConfig: countryConfig || null,
      schema: countryConfig?.schema || null,
      stateKey: null, stateInfo: null,
      category: isActive ? null : 'following',
      feed: null,
    });
  }, [currentCategory, selectedCountry, countryConfig, onFilterChange]);

  const handleCountrySelect = useCallback((key) => {
    closeAll();
    const { category, feeds, feed } = resolveCascade(key, null, null, { drillIntoCategory: true });
    setSelectedCountry(key);
    setSelectedState(null);
    setSelectedCategory(category);
    setSelectedFeedId(feed ? feed.id : null);
    fireChange(key, null, category, feed ? feed.id : null, feeds);
  }, [fireChange, closeAll, resolveCascade]);

  const handleStateSelect = useCallback((key) => {
    closeAll();
    const { category, feeds, feed } = resolveCascade(selectedCountry, key, null, { drillIntoCategory: true });
    setSelectedState(key);
    setSelectedCategory(category);
    setSelectedFeedId(feed ? feed.id : null);
    fireChange(selectedCountry, key, category, feed ? feed.id : null, feeds);
  }, [fireChange, selectedCountry, closeAll, resolveCascade]);

  const handleClearState = useCallback(() => {
    closeAll();
    const { category, feeds, feed } = resolveCascade(selectedCountry, null, null, { drillIntoCategory: true });
    setSelectedState(null);
    setSelectedCategory(category);
    setSelectedFeedId(feed ? feed.id : null);
    fireChange(selectedCountry, null, category, feed ? feed.id : null, feeds);
  }, [fireChange, selectedCountry, closeAll, resolveCascade]);

  const handleCategorySelect = useCallback((catKey) => {
    closeAll();
    const { category, feeds, feed } = resolveCascade(
      selectedCountry, selectedState, catKey || null, { drillIntoCategory: false }
    );
    setSelectedCategory(category);
    setSelectedFeedId(feed ? feed.id : null);
    fireChange(selectedCountry, selectedState, category, feed ? feed.id : null, feeds);
  }, [fireChange, selectedCountry, selectedState, closeAll, resolveCascade]);

  const handleFeedSelect = useCallback((feedId) => {
    closeAll();
    setSelectedFeedId(feedId);
    fireChange(selectedCountry, selectedState, selectedCategory, feedId, availableFeeds);
  }, [fireChange, selectedCountry, selectedState, selectedCategory, availableFeeds, closeAll]);

  const dateFields = useMemo(() => {
    const detectedFields = detectDateFields(data);
    const keys = new Set(detectedFields.map((f) => f.key));
    allowedOrderBy.forEach((key) => {
      if (!keys.has(key)) { detectedFields.push({ key, label: formatFieldLabel(key) }); keys.add(key); }
    });
    if (currentOrderBy && !keys.has(currentOrderBy)) {
      detectedFields.push({ key: currentOrderBy, label: formatFieldLabel(currentOrderBy) });
    }
    detectedFields.sort((a, b) => a.label.localeCompare(b.label));
    return detectedFields;
  }, [data, currentOrderBy, allowedOrderBy]);

  const handleSortSelect = useCallback((fieldKey) => {
    closeAll(); if (onSortChange) onSortChange(fieldKey);
  }, [onSortChange, closeAll]);

  const currentSortLabel = useMemo(() => {
    if (!currentOrderBy) return t('common:default', 'Default');
    const match = dateFields.find((f) => f.key === currentOrderBy);
    return match ? match.label : formatFieldLabel(currentOrderBy);
  }, [currentOrderBy, dateFields, t]);

  // Every hook above has already run, so bailing out here keeps the hook order
  // identical between renders where the bar is shown and where it is hidden.
  // Nothing below this line may call a hook.
  if (hiddenOnThisPath) return null;

  const countryDisplay = countryConfig
    ? <><span className="feed-filter-toggle-emoji">{countryConfig.flag}</span> {countryConfig.label}</>
    : t('common:selectEllipsis', 'Select…');

  const selectedStateData = (hasRegions && selectedState)
    ? regionsForCountry.find((s) => s.key === selectedState) || null
    : null;

  const levelFederalLabel = selectedCountry === 'uk' ? t('common:levelKingdom', 'Kingdom')
    : selectedCountry === 'canada' ? t('common:levelProvince', 'Province')
    : t('common:levelFederal', 'Federal');

  const levelDisplay = selectedStateData
    ? <><img src={selectedStateData.flag} alt={selectedStateData.name} className="feed-filter-toggle-img" /> {selectedStateData.name}</>
    : <><span className="feed-filter-toggle-emoji">🏛️</span> {levelFederalLabel}</>;

  // 'updates' and 'videos' are spelled out here rather than looked up in
  // availableCategories: neither is a config category, so the lookup would fall
  // back to the raw key.
  const selectedCategoryLabel = selectedCategory === 'following'
    ? t('common:following', 'Following')
    : selectedCategory === 'glance'
        ? t('common:glance', 'For You')
      : selectedCategory === 'videos'
        ? t('common:section_liveStreams', 'Live streams')
      : selectedCategory === 'updates'
        ? t('common:section_updates', 'Web Crawl Updates')
      : selectedCategory
        ? t(`common:section_${selectedCategory}`, availableCategories.find((c) => c.key === selectedCategory)?.label || selectedCategory)
        : t('common:allCategories', 'All Categories');

  const selectedFeedLabel = selectedFeedId
    ? (availableFeeds.find((f) => f.id === selectedFeedId)?.title || selectedFeedId)
    : t('common:all', 'All');

  const hasCategories = availableCategories.length > 0;
  const showComingSoon = !hasFederalFeeds && !isInStateMode;

  // The Feed dropdown used to be gated on a category being selected, which hid
  // it under "All Categories" — including right after a Country or Level change,
  // where a feed has just been auto-loaded and the user most needs to see (and
  // change) which one it is.
  //
  // It's now gated on there being an actual choice. With a single feed the
  // control offered nothing to pick: "All" was dropped as meaningless and the
  // toggle read out the one feed already selected, so a state's Updates branch
  // rendered "Web Crawl Updates" beside a Topic that said the same thing. The
  // feed is still auto-selected and loaded in that case — see resolveCascade —
  // there just isn't a dropdown for it.
  const showFeedDropdown = !isPageOwnedCategory(selectedCategory, isInStateMode) && availableFeeds.length > 1;

  return (
    <div className={`feed-filter-bar ${inline ? 'feed-filter-bar--inline' : ''} ${className}`}>

      {/* ── Country ── */}
      {!hideCountry && (
        <FilterDropdown
          label={t('common:country', 'Country')}
          isOpen={openDropdown === 'country'}
          onToggle={() => toggleDropdown('country')}
          displayContent={countryDisplay}
        >
          {Object.entries(COUNTRY_CONFIGS).map(([key, cfg]) => (
            <FilterItem key={key} active={selectedCountry === key} emoji={cfg.flag} onClick={() => handleCountrySelect(key)}>
              {cfg.label}
            </FilterItem>
          ))}
        </FilterDropdown>
      )}

      {/* ── Level ── */}
      {/* Gated on the country actually having region data, not on an explicit
          allowlist. A new country without regions renders nothing here instead
          of inheriting the US list. */}
      {!hideLevel && hasRegions && (
        <FilterDropdown
          label={t('common:level', 'Level')}
          isOpen={openDropdown === 'level'}
          onToggle={() => toggleDropdown('level')}
          displayContent={levelDisplay}
        >
          <FilterItem active={!selectedState} emoji="🏛️" onClick={handleClearState}>
            {levelFederalLabel}
          </FilterItem>
          <FilterSeparator />
          {regionsForCountry.map((s) => (
            <FilterItem key={s.key} active={selectedState === s.key} image={s.flag} onClick={() => handleStateSelect(s.key)}>
              {s.name}
            </FilterItem>
          ))}
        </FilterDropdown>
      )}

      {/* ── Topic ── */}
      {showComingSoon ? (
          <div className="feed-filter-group">
            <span className="feed-filter-label">{t('common:topic', 'Topic')}</span>
            <div className="feed-filter-coming-soon">{t('common:comingSoon', 'Coming soon')}</div>
          </div>
        ) : hasCategories ? (
          <FilterDropdown
            label={t('common:topic', 'Topic')}
            isOpen={openDropdown === 'topic'}
            onToggle={() => toggleDropdown('topic')}
            displayContent={selectedCategoryLabel}
          >
            {/* Glance (AI-ranked) and Recents are country-level, so only show
                them at the federal level, not in state mode. */}
            {!isInStateMode && !hideGlance && (
              <FilterItem active={selectedCategory === 'glance'} onClick={() => handleCategorySelect('glance')}>
                {t('common:glance', 'Government news at a glance')}
              </FilterItem>
            )}

            {!isInStateMode && !hideRecents && (
              <FilterItem active={selectedCategory === 'recent'} onClick={() => handleCategorySelect('recent')}>
                {t('common:section_recent', 'Recents')}
              </FilterItem>
            )}
            {!hideFollowing && (
              <FilterItem active={selectedCategory === 'following'} onClick={() => handleCategorySelect('following')}>
                {t('common:following', 'Following')}
              </FilterItem>
            )}

            {/* Live streams: live, scheduled and recorded streams from the
                reader's city, county, state and federal government, at
                /us/live-streams. US home page only; see `videosAvailable`. */}
            {videosAvailable && (
              <FilterItem active={selectedCategory === 'videos'} onClick={() => handleCategorySelect('videos')}>
                {t('common:section_liveStreams', 'Live streams')}
              </FilterItem>
            )}

            {/* Updates: recently published or changed pages on official sites,
                from the crawl service. Pinned here only at the federal level,
                where it isn't a config category and has nowhere else to go. In
                state mode it's a branch of createStateFeeds, so it renders in
                the lower list alongside Legislative / Executive / Judicial and
                `pinnedCategories` stops filtering it out down there. Countries
                the crawler doesn't index don't get it at all; see
                `updatesHidden`. */}
            {!updatesHidden && !isInStateMode && (
              <FilterItem active={selectedCategory === 'updates'} onClick={() => handleCategorySelect('updates')}>
                {t('common:section_updates', 'Web Crawl Updates')}
              </FilterItem>
            )}

            <FilterItem active={!selectedCategory} onClick={() => handleCategorySelect(null)}>
              {t('common:allCategories', 'All Categories')}
            </FilterItem>
            <FilterSeparator />
            {availableCategories.filter((cat) => !pinnedCategories.includes(cat.key)).map((cat) => (
              <FilterItem key={cat.key} active={selectedCategory === cat.key} onClick={() => handleCategorySelect(cat.key)}>
                {t(`common:section_${cat.key}`, cat.label)}
              </FilterItem>
            ))}
          </FilterDropdown>
        ) : null
      }

      {/* ── Feed ── */}
      {showFeedDropdown && (
        <FilterDropdown
          label={t('common:feed', 'Feed')}
          isOpen={openDropdown === 'feed'}
          onToggle={() => toggleDropdown('feed')}
          displayContent={selectedFeedLabel}
        >
          <FilterItem active={!selectedFeedId} onClick={() => handleFeedSelect(null)}>{t('common:all', 'All')}</FilterItem>
          <FilterSeparator />
          {availableFeeds.map((feed) => (
            <FilterItem
              key={feed.id}
              active={selectedFeedId === feed.id}
              onClick={() => handleFeedSelect(feed.id)}
            >
              {feed.title}
            </FilterItem>
          ))}
        </FilterDropdown>
      )}

      {/* ── Sort ── */}
      {!hideSortButton && dateFields.length > 0 && (
        <div className="feed-filter-sort-icon-group" ref={sortRef}>
          <button
            type="button"
            className={`feed-filter-sort-btn ${openDropdown === 'sort' ? 'active' : ''}`}
            onClick={() => toggleDropdown('sort')}
            title={t('common:sortBy', 'Sort: {{label}}', { label: currentSortLabel })}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M7 4v16m0 0l-4-4m4 4l4-4" /><path d="M17 20V4m0 0l4 4m-4-4l-4 4" />
            </svg>
          </button>
          {openDropdown === 'sort' && (
            <div className="feed-filter-dropdown feed-filter-sort-dropdown" ref={sortPanelRef} style={sortPanelStyle}>
              {dateFields.map((field) => (
                <FilterItem key={field.key} active={currentOrderBy === field.key} onClick={() => handleSortSelect(field.key)}>
                  {field.label}
                </FilterItem>
              ))}
            </div>
          )}
        </div>
      )}


    </div>
  );
}